import * as THREE from 'three';
import { bus } from '@/core/EventBus';

export interface CutsceneCameraBeat {
  readonly focus: THREE.Vector3;
  readonly viewHeight?: number;
  readonly ease?: 'linear' | 'smooth';
}

export interface CutsceneText {
  readonly speaker?: string;
  readonly line: string;
}

export interface CutsceneBeat {
  readonly duration: number;
  readonly camera?: CutsceneCameraBeat;
  readonly text?: CutsceneText;
  readonly action?: () => void;
}

export interface CutsceneSequence {
  readonly id: string;
  readonly beats: readonly CutsceneBeat[];
  readonly restoreCamera?: boolean;
}

export interface CameraDirector {
  readonly focus: THREE.Vector3;
  viewHeight: number;
  snapTo(target: THREE.Vector3): void;
}

export interface CutsceneSnapshot {
  readonly active: boolean;
  readonly sequenceId: string | null;
  readonly beatIndex: number;
  readonly beatElapsed: number;
  readonly inputLocked: boolean;
  readonly text: CutsceneText | null;
}

interface CameraRestorePoint {
  readonly focus: THREE.Vector3;
  readonly viewHeight: number;
}

export class CutscenePlayer {
  readonly #camera: CameraDirector;
  #sequence: CutsceneSequence | null = null;
  #beatIndex = 0;
  #beatElapsed = 0;
  #inputLocked = false;
  #restorePoint: CameraRestorePoint | null = null;
  #beatStartFocus = new THREE.Vector3();
  #beatStartViewHeight = 0;
  #text: CutsceneText | null = null;

  constructor(camera: CameraDirector) {
    this.#camera = camera;
  }

  get active(): boolean {
    return this.#sequence !== null;
  }

  get inputLocked(): boolean {
    return this.#inputLocked;
  }

  get text(): CutsceneText | null {
    return this.#text;
  }

  play(sequence: CutsceneSequence): void {
    if (sequence.beats.length === 0) throw new Error('Cutscene requires at least one beat');
    if (this.#sequence) this.#finish('ended');
    this.#sequence = sequence;
    this.#beatIndex = 0;
    this.#beatElapsed = 0;
    this.#inputLocked = true;
    this.#restorePoint = {
      focus: this.#camera.focus.clone(),
      viewHeight: this.#camera.viewHeight,
    };
    bus.emit('cutscene:started', { id: sequence.id });
    this.#enterBeat(0);
  }

  update(dt: number): void {
    if (!this.#sequence) return;
    const beat = this.#sequence.beats[this.#beatIndex]!;
    this.#beatElapsed += dt;
    this.#updateCamera(beat);

    if (this.#beatElapsed < beat.duration) return;
    const nextIndex = this.#beatIndex + 1;
    if (nextIndex >= this.#sequence.beats.length) {
      this.#finish('ended');
      return;
    }
    this.#enterBeat(nextIndex);
  }

  skip(): void {
    if (!this.#sequence) return;
    this.#finish('skipped');
  }

  snapshot(): CutsceneSnapshot {
    return {
      active: this.active,
      sequenceId: this.#sequence?.id ?? null,
      beatIndex: this.#beatIndex,
      beatElapsed: this.#beatElapsed,
      inputLocked: this.#inputLocked,
      text: this.#text ? { ...this.#text } : null,
    };
  }

  #enterBeat(index: number): void {
    const sequence = this.#sequence;
    if (!sequence) return;
    this.#beatIndex = index;
    this.#beatElapsed = 0;
    const beat = sequence.beats[index]!;
    this.#text = beat.text ?? null;
    this.#beatStartFocus.copy(this.#camera.focus);
    this.#beatStartViewHeight = this.#camera.viewHeight;
    beat.action?.();
    this.#updateCamera(beat);
    bus.emit('cutscene:beat', { id: sequence.id, index, ...(beat.text ? { text: beat.text } : {}) });
  }

  #updateCamera(beat: CutsceneBeat): void {
    if (!beat.camera) return;
    const progress = beat.duration <= 0 ? 1 : THREE.MathUtils.clamp(this.#beatElapsed / beat.duration, 0, 1);
    const t = beat.camera.ease === 'smooth' ? smoothstep(progress) : progress;
    this.#camera.focus.copy(this.#beatStartFocus).lerp(beat.camera.focus, t);
    this.#camera.snapTo(this.#camera.focus);
    if (beat.camera.viewHeight !== undefined) {
      this.#camera.viewHeight = THREE.MathUtils.lerp(this.#beatStartViewHeight, beat.camera.viewHeight, t);
    }
  }

  #finish(kind: 'ended' | 'skipped'): void {
    const sequence = this.#sequence;
    if (!sequence) return;
    const restore = sequence.restoreCamera ?? true;
    if (restore && this.#restorePoint) {
      this.#camera.viewHeight = this.#restorePoint.viewHeight;
      this.#camera.snapTo(this.#restorePoint.focus);
    }
    this.#sequence = null;
    this.#beatIndex = 0;
    this.#beatElapsed = 0;
    this.#inputLocked = false;
    this.#restorePoint = null;
    this.#text = null;
    bus.emit(kind === 'skipped' ? 'cutscene:skipped' : 'cutscene:ended', { id: sequence.id });
  }
}

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}
